import type { AircraftStatus, FleetAircraftView, LegView } from '../../domain/models/contracts';
import { formatPct } from '../format';

const STATUS_LABEL: Readonly<Record<AircraftStatus, string>> = {
  IN_FLIGHT: 'In Flight',
  GATE_TURNAROUND: 'Gate Turnaround',
  MAINTENANCE: 'Maintenance',
  IDLE: 'Idle',
};

function hhmm(minuteOfDay: number): string {
  const h = Math.floor(minuteOfDay / 60);
  const m = Math.floor(minuteOfDay % 60);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/** Time until an absolute simulation minute, given the current sim hour. */
function untilLabel(targetAbsMinute: number | null, nowHour: number): string {
  if (targetAbsMinute === null) return '—';
  const delta = Math.max(0, targetAbsMinute - nowHour * 60);
  if (delta < 60) return `${Math.round(delta)}m`;
  return `${(delta / 60).toFixed(1)}h`;
}

const STATUS_ORDER: readonly AircraftStatus[] = ['IN_FLIGHT', 'GATE_TURNAROUND', 'MAINTENANCE', 'IDLE'];

interface OperationsGridProps {
  fleet: readonly FleetAircraftView[];
  legs: readonly LegView[];
  hour: number;
}

function routeLabel(aircraft: FleetAircraftView, legsById: ReadonlyMap<string, LegView>): string {
  if (aircraft.currentLegId !== null) {
    const leg = legsById.get(aircraft.currentLegId);
    if (leg !== undefined) return `${leg.origin} → ${leg.destination}`;
  }
  return aircraft.locationAirportId;
}

function statusDetail(aircraft: FleetAircraftView, hour: number): string {
  switch (aircraft.status) {
    case 'IN_FLIGHT':
      return `lands in ${untilLabel(aircraft.statusUntilAbsMinute, hour)}`;
    case 'GATE_TURNAROUND':
      return `ready in ${untilLabel(aircraft.statusUntilAbsMinute, hour)}`;
    case 'MAINTENANCE':
      return `${aircraft.maintenanceKind ?? '?'}-check · back in ${untilLabel(aircraft.statusUntilAbsMinute, hour)}`;
    case 'IDLE':
      return aircraft.nextDepartureAbsMinute === null ? 'no schedule' : 'awaiting departure';
    default: {
      const exhaustive: never = aircraft.status;
      return exhaustive;
    }
  }
}

export function OperationsGrid({ fleet, legs, hour }: OperationsGridProps) {
  const legsById = new Map(legs.map((l) => [l.id, l] as const));
  const counts = new Map<AircraftStatus, number>();
  for (const aircraft of fleet) {
    counts.set(aircraft.status, (counts.get(aircraft.status) ?? 0) + 1);
  }
  const sorted = [...fleet].sort((a, b) => a.registration.localeCompare(b.registration));

  return (
    <div className="card-body ops-body">
      <div className="card-head">
        <h2>Live Operations</h2>
        <div className="chips">
          {STATUS_ORDER.map((status) => (
            <span key={status} className={`chip status-${status.toLowerCase()}`}>
              {STATUS_LABEL[status]} <strong>{counts.get(status) ?? 0}</strong>
            </span>
          ))}
        </div>
      </div>
      <div className="ops-scroll">
        <table className="grid ops-table">
          <colgroup>
            <col className="ops-col-aircraft" />
            <col className="ops-col-status" />
            <col className="ops-col-route" />
            <col className="ops-col-load" />
          </colgroup>
          <thead>
            <tr>
              <th>Aircraft</th>
              <th>Status</th>
              <th>Route / Location</th>
              <th className="num">Load Factor</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((aircraft) => {
              const nextMinuteOfDay =
                aircraft.nextDepartureAbsMinute === null ? null : aircraft.nextDepartureAbsMinute % 1440;
              return (
                <tr key={aircraft.registration}>
                  <td>
                    <strong>{aircraft.registration}</strong>
                    <div className="sub">
                      {aircraft.typeId} · {aircraft.purchaseType === 'PURCHASE' ? 'Owned' : 'Leased'}
                    </div>
                    <div className="sub">A-check in {Math.round(aircraft.hoursToACheck)} h</div>
                  </td>
                  <td>
                    <span className={`badge status-${aircraft.status.toLowerCase()}`}>
                      {STATUS_LABEL[aircraft.status]}
                    </span>
                    <div className="sub">{statusDetail(aircraft, hour)}</div>
                  </td>
                  <td>
                    {routeLabel(aircraft, legsById)}
                    <div className="sub">
                      {nextMinuteOfDay === null
                        ? 'No departure'
                        : `Next ${hhmm(nextMinuteOfDay)} · in ${untilLabel(aircraft.nextDepartureAbsMinute, hour)}`}
                    </div>
                  </td>
                  <td className="num">
                    <div className="bar" aria-hidden="true">
                      <div
                        className="bar-fill"
                        style={{ width: `${Math.min(100, aircraft.loadFactorPct)}%` }}
                      />
                    </div>
                    <span>{formatPct(aircraft.loadFactorPct)}</span>
                    <div className="sub">
                      {aircraft.status === 'IN_FLIGHT' ? `${aircraft.passengersOnboard}/${aircraft.seats}` : `of ${aircraft.seats}`}
                    </div>
                  </td>
                </tr>
              );
            })}
            {sorted.length === 0 && (
              <tr>
                <td colSpan={4} className="muted empty">
                  Waiting for simulation start...
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
